import { DecimalPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TestCase } from '@vendasta/protos/ai_assistants/v1alpha1';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import {
  DEFAULT_TARGET_PERCENT,
  MAX_TARGET_PERCENT,
  MIN_TARGET_PERCENT,
  targetPercentFromRate,
  targetPercentValidators,
} from '../../../core/utils/success-evaluators';
import { JobStat } from '../jobs-to-be-done.service';

/** The job the user described, shaped the way a create would send it. */
export type JobCreateModalResult = Pick<
  TestCase,
  'name' | 'scenario' | 'expectation' | 'isSuccessEvaluator' | 'successEvaluationTargetRate'
>;

/**
 * A job opened for editing, with how it is currently scoring: its pass rate (null when nothing
 * is scored yet), the scored and passing counts behind it, and the trailing window, in days,
 * they cover.
 */
export type JobCreateModalData = JobCreateModalResult &
  Pick<JobStat, 'successRate' | 'evaluatedCount' | 'passedCount' | 'evaluationWindowDays'>;

/**
 * Longest scenario the API accepts. It counts unicode code points, while both
 * this validator and the textarea's maxlength count UTF-16 units — so text
 * outside the basic plane is capped a little early rather than rejected on save.
 */
export const MAX_SCENARIO_LENGTH = 280;

@Component({
  selector: 'ai-job-create-modal',
  imports: [
    DecimalPipe,
    PercentPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './job-create-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './job-create-modal.component.scss',
})
export class JobCreateModalComponent {
  private readonly dialogRef = inject(MatDialogRef<JobCreateModalComponent, JobCreateModalResult>);
  /** The job being edited, or null when writing a new one. */
  private readonly job = inject<JobCreateModalData | null>(MAT_DIALOG_DATA, { optional: true });

  protected readonly maxScenarioLength = MAX_SCENARIO_LENGTH;
  protected readonly minTargetPercent = MIN_TARGET_PERCENT;
  protected readonly maxTargetPercent = MAX_TARGET_PERCENT;
  protected readonly isEdit = !!this.job;
  protected readonly successRate = this.job?.successRate ?? null;
  protected readonly evaluatedCount = this.job?.evaluatedCount ?? 0;
  protected readonly passedCount = this.job?.passedCount ?? 0;
  protected readonly evaluationWindowDays = this.job?.evaluationWindowDays ?? 0;

  protected readonly form = new FormGroup({
    name: new FormControl(this.job?.name ?? '', { nonNullable: true, validators: [Validators.required] }),
    scenario: new FormControl(this.job?.scenario ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(MAX_SCENARIO_LENGTH)],
    }),
    expectation: new FormControl(this.job?.expectation ?? '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    targetPercent: new FormControl<number | null>(targetPercentFromRate(this.job?.successEvaluationTargetRate), {
      validators: targetPercentValidators,
    }),
  });

  protected submit(): void {
    if (this.form.invalid) return;
    const { targetPercent, ...job } = this.form.getRawValue();
    this.dialogRef.close({
      ...job,
      isSuccessEvaluator: true,
      successEvaluationTargetRate: (targetPercent ?? DEFAULT_TARGET_PERCENT) / 100,
    });
  }

  /** Whether the target field is showing an error, which takes the place of its hint. */
  protected targetErrorShown(): boolean {
    const target = this.form.controls.targetPercent;
    return (
      (target.touched && target.hasError('required')) ||
      target.hasError('min') ||
      target.hasError('max') ||
      target.hasError('wholeNumber')
    );
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
